import { Box, Button, Flex, Input, InputGroup, InputLeftElement, Select, Image, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import MyDiv from "./employeeDocumentDrawer.style";
import UploadLogo from "../../../../../assets/images/UploadLogo.svg";
import { CustomButton } from "../../../../../components";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import { DocumentState, ICategory, IName, props } from "./model";

const EmployeeDocumentDrawer = ({ employeeId, onDocumentAdded, onClose }: props) => {
  const [document, setDocument] = useState<DocumentState>({ docType: "", docName: "", fileName: "", file: null });
  const [loading, setLoading] = useState(false);
  const [dnocumetCategory, setDocumetCategory] = useState<ICategory[]>([]);
  const [dnocumetName, setDocumentName] = useState<{ [key: number]: IName[] }>({});
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const handleNextClick = async () => {
    if (!document.docType) {
      addToast({ message: "Document Type is required.", status: "error" });
      return;
    }
    if (!document.docName) {
      addToast({ message: "Document Name is required.", status: "error" });
      return;
    }
    if (!document.file) {
      addToast({ message: "File upload is required.", status: "error" });
      return;
    }

    try {
      setLoading(true);
      const formDataPayload = new FormData();
      formDataPayload.append("employeeId", String(employeeId || ""));
      formDataPayload.append("documentCategoryId", document.docType);
      formDataPayload.append("documentNameId", document.docName);
      formDataPayload.append("documentNumber", document.number || "");
      formDataPayload.append("expiryDate", document.expiry || "");
      formDataPayload.append("file", document.file);

      const res = await fetchApi("Hrlense_Employee/EmpDocUpload", "POST", formDataPayload, null, "Document uploaded successfully", true);
      if (res) {
        setDocument({ docType: "", docName: "", fileName: "", file: null, number: "", expiry: "" });
        onDocumentAdded();
        onClose();
      }
    } catch (error) {
      addToast({ message: `Error uploading document: ${error instanceof Error ? error.message : "Unknown error"}`, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    setDocument({ ...document, docName: value, number: "", expiry: "" });
  };

  const handleTypeChange = async (value: string) => {
    setDocument({ ...document, docType: value, docName: "", number: "", expiry: "" });
    if (value) {
      await LoadDocName(parseInt(value));
    }
  };

  const handleNumberChange = (value: string) => {
    setDocument({ ...document, number: value });
  };

  const handleExpiryChange = (value: string) => {
    setDocument({ ...document, expiry: value });
  };

  const renderInputs = () => {
    const selectedName = dnocumetName[parseInt(document.docType)]?.find(n => n.id === parseInt(document.docName));
    if (!selectedName) return null;

    return (
      <>
        {selectedName.document_Number && (
          <div>
            <label className="font-poppins text_sm text_regular">Document Number</label>
            <Input
              placeholder="Enter Document Number"
              mb={3}
              value={document.number || ""}
              onChange={(e) => handleNumberChange(e.target.value)}
              className="font-poppins text_sm text_regular"
            />
          </div>
        )}

        {selectedName.expire && (
          <div>
            <label className="font-poppins text_sm text_regular">Expiry Date</label>
            <Input
              type="date"
              value={document.expiry || ""}
              onChange={(e) => handleExpiryChange(e.target.value)}
              className="font-poppins text_sm text_regular"
            />
          </div>
        )}
      </>
    );
  };

  const LoadDocName = async (category_Id: number) => {
    const response = await fetchApi("DropDown/DocumentName", "GET", null, `category_Id=${category_Id}`);
    if (response) {
      setDocumentName((prev) => ({ ...prev, [category_Id]: response }));
    }
  };

  const LoadDocCategory = async () => {
    const response = await fetchApi( "Hrlense_Document_Category/dropdown", "GET", null, null);
    if (response) {
      const employeeCategories = response.filter(
        (category: ICategory) => category.id !== 7
      );

      setDocumetCategory(employeeCategories);
    } else {
      setDocumetCategory([]);
    }
  };
  
  useEffect(() => {
    LoadDocCategory()
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

      if (allowedTypes.includes(file.type)) {
        setDocument({ ...document, file, fileName: file.name });
      } else {
        e.target.value = "";
        setDocument({ ...document, file: null, fileName: "" });
        addToast({ message: "Only PDF or image files (PNG/JPG) are allowed.", status: "error" });
      }
    }
  };

  return (
    <MyDiv>
      <Flex direction="column" height="100%">
        <Box py={2} flex="1" overflowY="auto">
          <Text className="font-poppins text_md text_medium" mb={2}>Upload Document</Text>
          <Box mb={4}>
            <label className="font-poppins text_sm text_regular">Document Category</label>
            <Select
              placeholder="Select-document-category"
              value={document.docType}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="font-poppins text_sm text_regular"
            >
              {dnocumetCategory.map((type: ICategory) => (
                <option key={type.id} value={type.id}>{type.document_Category_Name}</option>
              ))}
            </Select>
          </Box>

          <Box mb={4}>
            <label className="font-poppins text_sm text_regular">Document Name</label>
            <Select
              placeholder="Select-document-name"
              value={document.docName}
              onChange={(e) => handleNameChange(e.target.value)}
              className="font-poppins text_sm text_regular"
              isDisabled={!document.docType}
            >
              {(dnocumetName[parseInt(document.docType)] || []).map((name) => (
                <option key={name.id} value={name.id}>{name.document_Name_Title}</option>
              ))}
            </Select>
          </Box>

          <Box mb={4}>{renderInputs()}</Box>
          <Flex align="center" mb={4}>
            <InputGroup>
              <InputLeftElement width="auto" px={2}>
                <Button
                  as="label"
                  htmlFor={`file-upload`}
                  size="sm"
                  colorScheme="gray"
                  leftIcon={<Image src={UploadLogo} />}
                  cursor="pointer"
                  borderRadius="md"
                  w="100%"
                  className="font-poppins text_sm text_regular"
                >
                  Upload
                </Button>
                <input
                  id={`file-upload`}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/jpg"
                  hidden
                  onChange={handleFileChange}
                />
              </InputLeftElement>

              <Input
                className="font-poppins text_sm text_regular"
                placeholder="No file chosen"
                value={document.fileName}
                isReadOnly
                pl="120px"
              />
            </InputGroup>
          </Flex>
        </Box>

        <Box mt="auto" borderTop="1px solid #eee" pt={3}>
          <Flex justify="flex-end">
            <CustomButton
              title={loading ? "Uploading..." : "Upload Document"}
              className="btn_theme font-poppins text_sm text_medium"
              onClick={handleNextClick}
              isDisabled={loading}
            />
          </Flex>
        </Box>
      </Flex>
    </MyDiv>
  );
};

export default EmployeeDocumentDrawer;
